// Confirmed-live brand profiles. ONE source for both consumers: the visible
// footer links and Organization.sameAs in lib/structured-data.ts. Kept together
// so a profile can never be linked in the UI but missing from the schema (or
// vice versa) — the exact drift §7 of CLAUDE.md exists to prevent.
//
// sameAs is the single highest-leverage GEO signal (brand-entity presence
// correlates ~3x stronger with AI citations than backlinks), so fill it in —
// but add ONLY URLs verified to return 200 AND to be OURS. A wrong sameAs is a
// negative signal: it tells Google the brand IS that account.
//
// Confirmed-ours, supplied by the team and each verified to serve
// LeadsMiner-owned content (checked 2026-08-11, re-checked 2026-08-18):
//   instagram.com/leadsminer.app   -> "LeadsMiner (@leadsminer.app)", 47 posts
//   x.com/GetLeadsminer            -> "Leadsminer.app (@GetLeadsminer)"
//   facebook.com/...61586727310344 -> "LeadsMiner | Cupertino CA"
//     (curl gets 400 from Facebook's bot wall — that is scraper blocking, not a
//     dead page; ownership was confirmed by the team in a real browser.)
//   linkedin.com/company/leadsminerapp -> 200, URL supplied by the team 2026-10-05
//     (note the "app" suffix: the bare /company/leadsminer slug is a different,
//     unconfirmed page — see below)
//
// Do NOT guess a handle from the brand name. x.com/leadminer — the obvious
// guess — is "Mark P Alexander", a stranger with 15 followers.
//
// Deliberately omitted until ownership is confirmed (a NAME MATCH IS NOT PROOF):
//   linkedin.com/company/leadsminer -> 200, titled "Leads Miner" (NOT the page we use)
//   youtube.com/@leadsminer         -> 200, titled "leadsminer"
//   tiktok.com/@leadsminer.app      -> 200, but serves no verifiable content
//                                      to a non-JS client
// Add each here once the team confirms the account is ours — both the footer
// link and the schema pick it up automatically.

export type SocialProfile = {
  /** Accessible name — used for aria-label and title. */
  name: string;
  url: string;
  /** Single `d` path on a 24x24 viewBox (Simple Icons geometry). */
  path: string;
};

export const SOCIAL_PROFILES: SocialProfile[] = [
  {
    name: "LeadsMiner on X",
    url: "https://x.com/GetLeadsminer",
    path: "M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z",
  },
  {
    name: "LeadsMiner on Instagram",
    url: "https://www.instagram.com/leadsminer.app",
    path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
  },
  {
    name: "LeadsMiner on Facebook",
    url: "https://www.facebook.com/profile.php?id=61586727310344",
    path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  },
  {
    name: "LeadsMiner on LinkedIn",
    url: "https://www.linkedin.com/company/leadsminerapp/",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0z",
  },
];

/** URLs only, for Organization.sameAs. */
export const SOCIAL_PROFILE_URLS = SOCIAL_PROFILES.map((p) => p.url);
